import React, { useId } from "react";
import { useWeatherContext } from "../../_client/context";
import {
    BAR_DESKTOP_SPACING,
    BAR_DESKTOP_WIDTH,
    BAR_MOBILE_SPACING,
    BAR_MOBILE_WIDTH,
    CHART_DESKTOP_WIDTH,
    CHART_HEIGHT,
    CHART_MOBILE_WIDTH,
    FONT_SIZE,
} from "./constants";
import graphStyles from "./Graphs.module.scss";
import weatherStyles from "./Weather.module.scss";
import { MonthlyWeather } from "./types";

type RainfallGraphProps = {
    monthlyWeather: MonthlyWeather;
};

export const RainfallGraph: React.FC<RainfallGraphProps> = ({
    monthlyWeather,
}) => {
    const { isCustomary } = useWeatherContext();
    const gradientId = useId();

    const { avgMonthlyRainfallCm, avgMonthlyRainfallInch } = monthlyWeather;
    const monthlyRainfall = isCustomary
        ? avgMonthlyRainfallInch
        : avgMonthlyRainfallCm;
    const maxMonthlyRainfall = Math.max(...monthlyRainfall);

    return (
        <>
            <svg
                viewBox={`0 0 ${CHART_DESKTOP_WIDTH} ${CHART_HEIGHT}`}
                className={weatherStyles.desktop}
            >
                <defs>
                    <linearGradient
                        id={`RainfallGradientDesktop_${gradientId}`}
                        x1="0"
                        x2="0"
                        y1="0"
                        y2="1"
                    >
                        <stop
                            offset="0%"
                            stopColor="var(--s-gradient-dataviz-primary-start)"
                        />
                        <stop
                            offset="100%"
                            stopColor="var(--s-gradient-dataviz-primary-end)"
                        />
                    </linearGradient>
                </defs>
                {monthlyRainfall.map((value, index) => {
                    const height = (value * 100) / maxMonthlyRainfall || 0;
                    return (
                        <g key={index}>
                            <rect
                                width={BAR_DESKTOP_WIDTH}
                                height={height}
                                x={index * BAR_DESKTOP_SPACING}
                                y={CHART_HEIGHT - height} // SVG draws upside down
                                rx={0.5}
                                ry={0.5}
                                fill={`url(#RainfallGradientDesktop_${gradientId})`}
                            />
                            <text
                                textAnchor="middle"
                                x={
                                    index * BAR_DESKTOP_SPACING +
                                    BAR_DESKTOP_WIDTH / 2
                                }
                                y={CHART_HEIGHT - height - FONT_SIZE} // SVG draws upside down
                                fontSize={FONT_SIZE}
                                className={graphStyles["max-points"]}
                            >
                                {value}
                            </text>
                        </g>
                    );
                })}
            </svg>
            <svg
                viewBox={`0 0 ${CHART_MOBILE_WIDTH} ${CHART_HEIGHT}`}
                className={weatherStyles.mobile}
            >
                <defs>
                    <linearGradient
                        id={`RainfallGradientMobile_${gradientId}`}
                        x1="0"
                        x2="0"
                        y1="0"
                        y2="1"
                    >
                        <stop
                            offset="0%"
                            stopColor="var(--s-gradient-dataviz-primary-start)"
                        />
                        <stop
                            offset="100%"
                            stopColor="var(--s-gradient-dataviz-primary-end)"
                        />
                    </linearGradient>
                </defs>
                {monthlyRainfall.map((value, index) => {
                    const height = (value * 100) / maxMonthlyRainfall || 0;
                    return (
                        <g key={index}>
                            <rect
                                width={BAR_MOBILE_WIDTH}
                                height={height}
                                x={index * BAR_MOBILE_SPACING}
                                y={CHART_HEIGHT - height} // SVG draws upside down
                                rx={0.5}
                                ry={0.5}
                                fill={`url(#RainfallGradientMobile_${gradientId})`}
                            />
                            <text
                                textAnchor="middle"
                                x={
                                    index * BAR_MOBILE_SPACING +
                                    BAR_MOBILE_WIDTH / 2
                                }
                                y={CHART_HEIGHT - height - 12} // SVG draws upside down
                                fontSize={12}
                                className={graphStyles["max-points"]}
                            >
                                {value}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </>
    );
};
